<template>
  <div class="advanced-search">
    <div class="advanced-search__container">
      <search-input
        v-model="query"
        :placeholder="i18n.searchBox"
        @keyup="search"
      >
        <template #search>
          <slot name="search" />
        </template>
        <c-button
          use-custom-button="secondary"
          class="advanced-search__button"
          @click="search"
        >
          {{ i18n.searchButton }}
        </c-button>
      </search-input>

      <c-title
        v-if="!isEmpty(state.results)"
        size="h2"
        color="#1A202C"
        class="advanced-search__description"
      >
        <h2>{{ i18n.resultsShowing }} "{{ state.query }}"</h2>
      </c-title>

      <c-title
        v-else-if="state.query && isEmpty(state.results)"
        size="h2"
        color="#1A202C"
        class="advanced-search__description advanced-search--notfound"
      >
        <h2>
          {{ i18n.resultsNotFound }}
        </h2>
      </c-title>

      <h3
        v-if="filteredContentPages.length"
        class="advanced-search__internalTitle"
      >
        {{ i18n.internalPageTitle }}
      </h3>

      <advanced-search-result
        v-for="(result, index) in filteredContentPages"
        :key="index"
        :thumbnail="result.image"
        :alt="result.alt"
        :link="result.link"
        :title="result.title"
        :description="result.text"
        :query="query"
      />

      <h3
        v-if="filteredNews.length"
        class="advanced-search__internalTitle"
      >
        {{ i18n.internalNewsTitle }}
      </h3>

      <advanced-search-result
        v-for="(result, index) in filteredNews"
        :key="index"
        :link="result.link"
        :title="result.title"
        :description="result.text"
        :categories="result.categories[0].toString()"
        :date="result.date"
        :query="query"
      />

      <h3
        v-if="filteredPressReleases.length"
        class="advanced-search__internalTitle"
      >
        {{ i18n.internalPressTitle }}
      </h3>

      <advanced-search-result
        v-for="(result, index) in filteredPressReleases"
        :key="index"
        :link="result.link"
        :title="result.title"
        :description="result.text"
        :categories="result.categories[0].toString()"
        :date="result.date"
        :query="query"
      />

      <paginator
        v-if="state.total"
        :current="current"
        :i18n="i18n"
        :total="state.total"
        :limit="limit"
        scroll-to="advanced-search"
        @paginate="handlePagination"
      />
      <p
        v-else
        class="advanced-search__noResults"
      >
&nbsp;
      </p>
    </div>
    <img
      aria-hidden="true"
      :src="imageSrc"
      class="advanced-search__decorator"
      alt=""
    >
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import { mapState } from 'vuex'
import DecoratorMobile from '@images/redesign/decorator-search-mobile.png'
import DecoratorDesktop from '@images/redesign/decorator-search-desktop.png'
import CTitle from '@atoms/title/title.vue'
import CButton from '@atoms/button/button.vue'
import SearchInput from '@molecules/search-input/search-input.vue'
import Paginator from '@molecules/paginator/paginator.vue'
import Result from './advanced-search-result.vue'
import API from './api'

export default {
  name: 'advanced-search',
  components: {
    Paginator,
    'advanced-search-result': Result,
    CButton,
    CTitle,
    SearchInput,
  },
  props: {
    api: {
      type: String,
      required: true,
    },
    i18n: {
      type: Object,
      default() {
        return {
          filterTitle: 'Filter',
          filterClear: 'Clear',
          filterNotAvaliable: 'No available filters',
          searchBox: 'New search',
          searchButton: 'Search',
          resultsShowing: 'Showing results for',
          resultsNotFound: 'No results found',
          paginatorPrevious: 'Previous',
          paginatorNext: 'Next',
          pageSize: 10,
          internalPageTitle: 'Pages',
          internalNewsTitle: 'News',
          internalPressTitle: 'Press releases',
        }
      },
    },
    limit: {
      type: Number,
      default: 4,
    },
  },
  data() {
    return {
      DecoratorMobile,
      DecoratorDesktop,
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    ...mapState({
      state: (state) => state.search,
    }),
    current: {
      get() {
        return this.$store.state.search.current
      },
      set(current) {
        this.$store.commit('search/paginate', current)
      },
    },
    imageSrc() {
      return this.windowWidth < 1400
        ? this.DecoratorMobile
        : this.DecoratorDesktop
    },
    filteredPressReleases() {
      return this.state.results.filter(
        (result) => result.type === 'Press Release'
      )
    },
    filteredNews() {
      return this.state.results.filter((result) => result.type === 'News')
    },
    filteredContentPages() {
      return this.state.results.filter((result) => result.type === 'Content')
    },
  },
  watch: {
    'state.query': function () {
      this.pushGTM(this.state.query)
    }
  },
  created() {
    Object.assign(API.options, {
      search: this.api,
    })

    const params = new URLSearchParams(window.location.search)
    const query = params.get('k')

    this.query = query
    this.$store.commit('search/search', query)
  },
  mounted() {
    this.$store.state.search.pageSize = this.i18n.pageSize
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...{ isEmpty },
    search(event) {
      if (event.type !== 'click' && event.key !== 'Enter') {
        return
      }

      this.searchOrigin = 'hub'

      if (window.history.replaceState) {
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}?k=${this.query}`
        )
        this.$store.commit('search/search', this.query)
      } else {
        window.location.search = `k=${this.query}`
      }
    },
    ellipsis(text) {
      const limit = 280

      if (typeof text !== 'string') {
        return ''
      }

      return text.length > limit ? `${text.substr(0, limit - 1)}...` : text
    },
    handlePagination(page) {
      this.current = page
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    pushGTM(query) {
      const origin = this.searchOrigin || 'header'

      if (!query || query.trim() === '') {
        return
      }

      window.dataLayer.push({
        event: 'search',
        section: origin,
        name: query,
        quantity: this.state.numberOfResults,
      })
    }

  },
}
</script>
